import { Download, Loader2, createLucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KeyValueList } from '@/components/ui/key-value-list';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import type { OpportunityDetail } from '@/hooks/api/useOpportunities';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { policyHasIncomeFeature } from '@/lib/income';

const DownloadOff = createLucideIcon('download-off', [
  ['path', { d: 'M12 15V3', key: 'shaft' }],
  ['path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', key: 'tray' }],
  ['path', { d: 'm7 10 5 5 5-5', key: 'chevron' }],
  ['path', { d: 'm2 2 20 20', key: 'slash' }],
]);

type PolicyData = OpportunityDetail['policy'];

interface PolicyCardProps {
  policy: PolicyData;
  advisorName?: string | null;
  advisorStatus?: string | null;
  columns?: 2 | 4;
  onDownloadStatement?: () => void;
  isCheckingStatement?: boolean;
}

function formatPolicyStatus(status: string | null): string {
  if (!status) return '—';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatSurrenderCharge(value: number | null): string {
  if (value === null) return '—';
  const percentValue = value > 0 && value < 1 ? value * 100 : value;
  return `${percentValue.toFixed(2)}%`;
}

function formatAllocationRate(value: number | null): string {
  if (value === null) return '—';
  return `${(value * 100).toFixed(2)}%`;
}

function formatRateType(value: string | null): string {
  if (!value) return 'Rate';
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
}

function PolicyShell({ children }: { children: ReactNode }) {
  return (
    <Card
      className="h-full overflow-hidden border border-border bg-detail-surface"
      style={{
        boxShadow: 'var(--shadow-2), var(--inset-highlight)',
      }}
    >
      {children}
    </Card>
  );
}

export function PolicyCard({
  policy,
  advisorName,
  advisorStatus,
  columns = 2,
  onDownloadStatement,
  isCheckingStatement,
}: PolicyCardProps) {
  const productName = policy.productName?.trim() || '';
  const productType = policy.productType?.trim() || '';
  const productDisplay = productName || productType || '—';
  const policyNumber = policy.policyNumber ?? policy.contractNumber;
  const allocations = policy.fundingAllocations ?? [];

  // Derived: surrender penalty
  const surrenderPenalty =
    policy.accountValue != null && policy.surrenderValue != null
      ? policy.accountValue - policy.surrenderValue
      : null;

  const hasIncomeFeature = policyHasIncomeFeature(policy);

  const items = [
    {
      label: 'Advisor Name',
      // T20-3323: status in parentheses rather than its own labelled row, to
      // save vertical space on this card. With no status the name renders
      // alone -- never as "Jane Smith ()".
      value: advisorName
        ? advisorStatus
          ? `${advisorName} (${advisorStatus})`
          : advisorName
        : undefined,
    },
    { label: 'Carrier', value: policy.carrierName },
    { label: 'Policy Number', value: policyNumber },
    {
      label: 'Product',
      value: productDisplay,
    },
    { label: 'Product Type', value: productType || undefined },
    { label: 'Tax Qualification', value: policy.taxQualification || undefined },
    {
      label: 'Status',
      value: (
        <Badge variant={policy.policyStatus === 'Active' ? 'success' : 'secondary'}>
          {formatPolicyStatus(policy.policyStatus)}
        </Badge>
      ),
    },
    {
      label: 'Policy Age',
      value: policy.policyAge != null ? `${policy.policyAge} years` : undefined,
    },
    { label: 'Issue Date', value: formatDate(policy.issueDate) },
    { label: 'Surrender Date', value: formatDate(policy.surrenderDate) },
    // Maturity date omitted — rarely actionable for replacement decisions
    {
      label: 'Issued Premium',
      value: <span className="font-tabular">{formatCurrency(policy.premium)}</span>,
    },
    {
      label: 'Cost Basis',
      value: <span className="font-tabular">{formatCurrency(policy.costBasis)}</span>,
    },
    {
      label: 'Account Value',
      value: <span className="font-tabular">{formatCurrency(policy.accountValue)}</span>,
    },
    {
      label: 'Surrender Value',
      value: <span className="font-tabular">{formatCurrency(policy.surrenderValue)}</span>,
    },
    {
      label: 'Surrender Charge',
      value:
        policy.surrenderChargePercentage != null ? (
          <span className="font-tabular">
            {formatSurrenderCharge(policy.surrenderChargePercentage)}
          </span>
        ) : undefined,
    },
    {
      label: 'Surrender Penalty',
      value:
        surrenderPenalty != null ? (
          <span className="font-tabular text-destructive">{formatCurrency(surrenderPenalty)}</span>
        ) : undefined,
    },
    {
      label: 'Income Feature',
      value: hasIncomeFeature ? 'Yes' : 'No',
    },
    {
      label: 'Income Status',
      value: hasIncomeFeature ? (policy.incomeStatus === 'active' ? 'Active' : 'Not Started') : '—',
    },
  ];

  if (hasIncomeFeature && policy.incomeBenefitValue != null) {
    items.push({
      label: 'Income Account Value',
      value: <span className="font-tabular">{formatCurrency(policy.incomeBenefitValue)}</span>,
    });
  }

  if (policy.incomeStartDate) {
    items.push({ label: 'Income Start Date', value: formatDate(policy.incomeStartDate) });
  }

  return (
    <PolicyShell>
      <div className="relative min-h-0 xl:block">
        <section className="xl:w-[63%]">
          <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
            <div className="flex items-center gap-2">
              <CardTitle>Policy Information</CardTitle>
              {isCheckingStatement !== undefined &&
                (isCheckingStatement ? (
                  <span title="Checking for annual statement…" className="inline-flex">
                    <Loader2
                      className="h-4 w-4 animate-spin text-muted-foreground"
                      aria-label="Checking for annual statement"
                    />
                  </span>
                ) : onDownloadStatement ? (
                  <button
                    type="button"
                    onClick={onDownloadStatement}
                    title="Download Annual Statement"
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                ) : (
                  <span title="No annual statement available" className="inline-flex">
                    <DownloadOff
                      className="h-4 w-4 text-muted-foreground"
                      aria-label="No annual statement available"
                    />
                  </span>
                ))}
            </div>
          </CardHeader>
          <CardContent>
            <KeyValueList boldValues columns={columns} items={items} />
          </CardContent>
        </section>

        <section className="min-h-0 border-t border-border xl:absolute xl:inset-y-0 xl:right-0 xl:w-[37%] xl:border-t-0 xl:pb-2 xl:pt-4">
          <div className="flex h-full min-h-0 flex-col overflow-hidden xl:border-l xl:border-muted-foreground/60">
            <CardHeader className="pb-3 xl:pt-2">
              <CardTitle>Current Strategies</CardTitle>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col xl:pb-2">
              {allocations.length > 0 ? (
                <div className="relative min-h-0 flex-1">
                  <div className="scrollbar-hide h-full overflow-y-auto pr-2 pb-5">
                    {allocations.map((allocation, index) => {
                      const rates = [
                        ...(allocation.depositRatesDetails ?? []),
                        ...(allocation.indexDurationRatesDetails ?? []),
                      ];

                      return (
                        <div
                          key={`${allocation.fundAllocationName ?? 'allocation'}-${index}`}
                          className="border-b border-border/70 py-2.5 first:pt-0 last:border-b-0 last:pb-0"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <TruncatedTooltip
                                text={allocation.fundAllocationName ?? 'Unnamed allocation'}
                                className="text-sm font-medium"
                              />
                              <p className="mt-1 text-xs text-muted-foreground">
                                {formatDate(allocation.depositEndDate)}
                              </p>
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="text-sm font-semibold font-tabular">
                                {allocation.fundPercentage != null
                                  ? `${allocation.fundPercentage.toFixed(0)}%`
                                  : '—'}
                              </p>
                              <p className="mt-1 text-xs text-muted-foreground font-tabular">
                                {formatCurrency(allocation.fundValue)}
                              </p>
                            </div>
                          </div>

                          {rates.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {rates.map((rate, rateIndex) => (
                                <Badge
                                  key={`${rate.rateType ?? 'rate'}-${rateIndex}`}
                                  variant="outline"
                                  className="text-[11px] font-medium"
                                >
                                  {formatRateType(rate.rateType)} {formatAllocationRate(rate.rate)}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="current-strategies-fade pointer-events-none absolute inset-x-0 bottom-0 h-6" />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No current strategy allocations available
                </p>
              )}
            </CardContent>
          </div>
        </section>
      </div>
    </PolicyShell>
  );
}
